/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraControllerContextIdentifier,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  GamoraUUIDGuard,
  assert,
  createUUIDv4,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakErrorProtocolAuthenticationError,
  HoruzProtocolChulakErrorProtocolAuthenticationTimeout,
  HoruzProtocolChulakErrorProtocolInvalidMessage,
} from '../../errors/index.js';
import {
  HoruzProtocolChulakServerStateInvoker,
  HoruzProtocolChulakServerStateInvokerCreateServiceSessionInput,
  HoruzProtocolChulakServerStateInvokerCreateServiceSessionResult,
  HoruzProtocolChulakServerStateInvokerVerifyAuthenticationInput,
  HoruzProtocolChulakServerStateInvokerVerifyAuthenticationResult,
} from './enums.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessageAuthCompletedGuard } from '../../guards/message/auth-completed.js';
import { HoruzProtocolChulakMessageAuthGuard } from '../../guards/message/auth.js';
import { HoruzProtocolChulakServerSessionGuard } from '../../guards/server-session.js';
import { HoruzProtocolChulakServerStateBase } from './base.js';
import { HoruzProtocolChulakServerStateContext } from '../context.js';
import { HoruzProtocolChulakServerStateMap } from '../../statemap.js';
import { HoruzProtocolChulakServiceSessionGuard } from '../../guards/service-session.js';
import { HoruzProtocolChulakTaskAuthenticationTimer } from '../../tasks/index.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:server:auth',
});

/**
 * @public
 */
export type HoruzProtocolChulakServerStateAuthConfiguration = {
  defaultAuthenticationTimer: number;
};

/**
 * Input
 * @public
 */
export type HoruzProtocolChulakServerStateAuthUpsertChulakSessionInput = {
  serverSessionUuid: GamoraUUIDGuard;
};

/**
 * Server
 *
 * Initialization state
 * @public
 */
export class HoruzProtocolChulakServerStateAuth extends HoruzProtocolChulakServerStateBase<
  typeof HoruzProtocolChulakServerStateMap,
  HoruzProtocolChulakServerStateContext,
  IHoruz
> {
  /**
   * @public
   */
  private _configuration: HoruzProtocolChulakServerStateAuthConfiguration = {
    defaultAuthenticationTimer: 1000 * 5,
  };

  /**
   * Constructor
   * @param args The arguments
   */
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);
  }

  /**
   * Create or get server session
   * @param stateContext The state context
   * @param input The input parameters
   */
  private _createOrGetServerSession = async (
    serverStateContext: HoruzProtocolChulakServerStateContext,
    serverSessionUuid: GamoraUUIDGuard,
  ) => {
    const hasServerSession =
      await this.serverSessionStore.has(serverSessionUuid);
    if (hasServerSession) {
      // Restore previous server session
      const serverSession =
        await this.serverSessionStore.get(serverSessionUuid);
      debug.log(
        '(_upsertServerSession): Restored previous chulak server session. ',
      );
      return serverSession;
    } else {
      // Create new session
      const serverSession = HoruzProtocolChulakServerSessionGuard.Empty();
      serverSession.uuid = serverSessionUuid;
      debug.log('(_upsertServerSession): Created new chulak server session. ');
      return serverSession;
    }
  };

  /**
   * Create service session
   * @param stateContext The state context
   * @param input The input parameters
   */
  private _createOrGetServiceSession = async (
    serverStateContext: HoruzProtocolChulakServerStateContext,
    serviceSessionUuid: GamoraUUIDGuard | null,
  ) => {
    if (serviceSessionUuid) {
      const serviceSession =
        await this.serviceSessionStore.get(serviceSessionUuid);
      debug.log(
        '(_createOrGetServiceSession): Restored previous chulak service session. ',
      );
      return serviceSession;
    } else {
      // Create new session
      const serviceSession = HoruzProtocolChulakServiceSessionGuard.Empty();
      serviceSession.uuid = GamoraUUIDGuard.fromJSON(createUUIDv4());
      debug.log(
        '(_upsertServiceSession): Created new chulak service session. ',
      );
      return serviceSession;
    }
  };

  /**
   * Event on message
   * @param contextIdentifier The context identifier
   */
  private _onQueueNext = (
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ) => {
    return this.$tryPostTask(serverStateContext, async () => {
      try {
        // Execute state
        const result = await this.recv(serverStateContext);
        if (result.done === false) {
          // Decode message type
          switch (result.message.type) {
            case HoruzProtocolChulakDataType.AUTH: {
              await this._onAuthMessage(serverStateContext, result.message);
              break;
            }
            default: {
              throw HoruzProtocolChulakErrorProtocolInvalidMessage.Create(
                'Message ' +
                  result.message.type +
                  ' not supported in Auth state.',
              );
            }
          }
        }
      } catch (e: any) {
        debug.log(
          '(_onAuthMessage): Unable to process authentication messages. ',
        );
        debug.trace(e?.message, e?.stack);
        this._tryHandleException(serverStateContext, e);
      }
    });
  };

  /**
   * Handle authentication
   */
  private async _onAuthMessage(
    serverStateContext: HoruzProtocolChulakServerStateContext,
    message: HoruzProtocolChulakMessageAuthGuard,
  ) {
    try {
      // Execute invocation
      const verifyAuthenticationResult =
        await this.$executeInvocation<HoruzProtocolChulakServerStateInvokerVerifyAuthenticationResult>(
          serverStateContext.contextIdentifier,
          HoruzProtocolChulakServerStateInvoker.VerifyAuthentication,
          serverStateContext,
          {
            compact: message.compact,
          } as HoruzProtocolChulakServerStateInvokerVerifyAuthenticationInput,
        );

      // Stop authentication timeout task
      await this._unregisterAuthenticationTimerTask(serverStateContext);

      // Needs to be locked since two connections could come in at the same time
      return await this.serverSessionStore.withLock(
        verifyAuthenticationResult.jti,
        async () => {
          try {
            // Get chulak session or create one if not exist
            const serverSession = await this._createOrGetServerSession(
              serverStateContext,
              verifyAuthenticationResult.jti.clone(),
            );

            // Holds the UUID of the current serivce session or null when a new one needs to be created
            let nullableServiceSessionUuid = null;

            // Check if service session already exists for jti
            if (
              serverSession.hasActiveServiceSessionUuidByServiceName(
                verifyAuthenticationResult.serviceName,
              )
            ) {
              nullableServiceSessionUuid =
                serverSession.getActiveServiceSessionUuidByServiceName(
                  verifyAuthenticationResult.serviceName,
                )!;
            }

            // Check if we should recreate the session
            if (message.fresh) {
              nullableServiceSessionUuid = null;
            }

            // Check if we need to create a new service session
            const serviceSession = await this._createOrGetServiceSession(
              serverStateContext,
              nullableServiceSessionUuid,
            );

            // Set expiration time for service session
            serviceSession.exp =
              verifyAuthenticationResult.exp - Math.ceil(Date.now() / 1000);
            assert(
              serviceSession.exp >= 10,
              'Invalid expiration date: expiration too close in future or already passed.',
            );

            // Create new underlying service session if null
            if (!nullableServiceSessionUuid) {
              // Set tags to service session
              const createServiceSessionResult =
                await this.$executeInvocation<HoruzProtocolChulakServerStateInvokerCreateServiceSessionResult>(
                  serverStateContext.contextIdentifier,
                  HoruzProtocolChulakServerStateInvoker.CreateServiceSession,
                  serverStateContext,
                  {
                    serviceSessionUuid: serviceSession.uuid,
                    compact: message.compact,
                  } as HoruzProtocolChulakServerStateInvokerCreateServiceSessionInput,
                );

              // Set tags
              serviceSession.tags = createServiceSessionResult.tags;

              // Sync Service session
              await this.serviceSessionStore.upsert(serviceSession);

              // Add service session to server session
              serverSession.addServiceSessionUuidByServiceName(
                verifyAuthenticationResult.serviceName,
                serviceSession.uuid,
              );
            }

            // Sync server session
            await this.serverSessionStore.upsert(serverSession);

            // Create auth completed message for this service session
            const authCompleted =
              HoruzProtocolChulakMessageAuthCompletedGuard.fromJSON({
                serviceSession: serviceSession.toJSON(),
              });

            // Send authentication completed message
            await this.send(serverStateContext, authCompleted);

            // Set uuids to context
            serverStateContext.stateContextRegistry.serverSessionUuid =
              serverSession.uuid;
            serverStateContext.stateContextRegistry.serviceSessionUuid =
              serviceSession.uuid;

            // Transition to open when active
            this.$postTransition(serverStateContext, { stateNext: 'Open' });
          } catch (e: any) {
            debug.log('(_onAuthMessage): Unable to setup serverState. ');
            debug.trace(e?.message, e?.stack);

            // Handle exception
            this._tryHandleException(serverStateContext, e);
          }
        },
      );
    } catch (e: any) {
      debug.log('(_onAuthMessage): Unable to authenticate request. ');
      debug.trace(e?.message, e?.stack);

      // Try to handle exception
      this._tryHandleException(
        serverStateContext,
        HoruzProtocolChulakErrorProtocolAuthenticationError.Create(),
      );
    }
  }

  /**
   * Initialize timers
   * @param stateContext
   */
  private _registerAuthenticationTimerTask = async (
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ) => {
    // Create authorization timer
    const authorizationTimerTask =
      new HoruzProtocolChulakTaskAuthenticationTimer({
        namespace: serverStateContext.contextIdentifier,
        name: 'authenticationTimeout',
        callback: async () => {
          if (this.$isInCurrentState(serverStateContext)) {
            debug.log(
              '(_initializeAuthenticationTimerTimer): Authentication timeout on ' +
                serverStateContext.contextIdentifier +
                '.',
            );
            this._tryHandleException(
              serverStateContext,
              HoruzProtocolChulakErrorProtocolAuthenticationTimeout.Create(
                'Authentication timeout.',
              ),
            );
          }
        },
        ms: this._configuration.defaultAuthenticationTimer,
      });

    // Start task
    await authorizationTimerTask.$start();

    // Register task in manager
    this.$root
      .$('protocols')
      .$('chulak')
      .$('tasks')
      .$('manager')
      .$registerTask(authorizationTimerTask);
  };

  /**
   * Initialize timers
   * @param stateContext
   */
  private _unregisterAuthenticationTimerTask = async (
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ) => {
    // Get task
    const authorizationTimerTask = this.$root
      .$('protocols')
      .$('chulak')
      .$('tasks')
      .$('manager')
      .$getTask(serverStateContext.contextIdentifier, 'authenticationTimeout');

    // Stop task
    await authorizationTimerTask.$terminate();

    // Unregister task in manager
    this.$root
      .$('protocols')
      .$('chulak')
      .$('tasks')
      .$('manager')
      .$unregisterTask(authorizationTimerTask);
  };

  /**
   * Enter state
   */
  override async $enter(
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ): Promise<void> {
    try {
      // Initialize timers
      await this._registerAuthenticationTimerTask(serverStateContext);

      // Listen for messages
      this.$root
        .$('queues')
        .$('data')
        .$on(serverStateContext.contextIdentifier, 'next', () =>
          this._onQueueNext(serverStateContext),
        );
    } catch (e) {
      this._tryHandleException(serverStateContext, e);
    }
  }

  /**
   * Exit state
   */
  override async $exit(
    serverStateContext: HoruzProtocolChulakServerStateContext,
  ): Promise<void> {
    this.$root
      .$('queues')
      .$('data')
      .$removeEventListenersByNamespaceAndName(
        serverStateContext.contextIdentifier,
        'next',
      );
  }

  /**
   * Register invokers
   */
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakServerStateInvoker.VerifyAuthentication,
    listener: (
      serverStateContext: HoruzProtocolChulakServerStateContext,
      input: HoruzProtocolChulakServerStateInvokerVerifyAuthenticationInput,
    ) => Promise<HoruzProtocolChulakServerStateInvokerVerifyAuthenticationResult>,
  ): this;
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakServerStateInvoker.CreateServiceSession,
    listener: (
      serverStateContext: HoruzProtocolChulakServerStateContext,
      input: HoruzProtocolChulakServerStateInvokerCreateServiceSessionInput,
    ) => Promise<HoruzProtocolChulakServerStateInvokerCreateServiceSessionResult>,
  ): this;
  invoker<K extends keyof typeof HoruzProtocolChulakServerStateInvoker>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    return super.$registerInvoker(contextIdentifier, name, listener);
  }
}
